import React from 'react';
import { Link } from 'react-router-dom';
import { EnvelopeIcon, PhoneIcon, ClockIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

export const DataDeletion = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="text-xl font-bold text-primary-600">Drive On Wheels</div>
          <Link to="/login" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
            Login →
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <article className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Data Deletion Instructions</h1>
          <p className="text-sm text-gray-500 mb-8">Last updated: May 23, 2026</p>

          <p className="text-gray-700 leading-relaxed mb-6">
            Drive On Wheels respects your right to control your personal data. This page explains how
            you can request deletion of your data from our Lead Management System (LMS) and any data
            connected to your Facebook account through our integration.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">How to Request Data Deletion</h2>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 mb-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-3">Email Request</h3>
            <p className="text-blue-800 mb-3">
              Send an email to <a href="mailto:padeyvarun03@gmail.com" className="font-semibold underline">padeyvarun03@gmail.com</a> with the following information:
            </p>
            <ul className="list-disc pl-6 space-y-1 text-blue-800">
              <li><strong>Subject:</strong> "Data Deletion Request"</li>
              <li>Your registered email address or phone number</li>
              <li>Your full name as registered</li>
              <li>Reason for deletion (optional)</li>
            </ul>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">What Gets Deleted</h2>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>Your user account and login credentials</li>
            <li>Personal information (name, email, phone, address)</li>
            <li>Any leads, follow-ups, or activities you created</li>
            <li>Facebook connection tokens and authorization data</li>
            <li>Usage logs and analytics data associated with your account</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Facebook Connection Removal</h2>
          <p className="text-gray-700 leading-relaxed mb-3">
            If you only want to disconnect your Facebook account (without deleting your LMS data):
          </p>
          <ol className="list-decimal pl-6 space-y-2 text-gray-700">
            <li>Log in to your LMS account</li>
            <li>Go to <strong>Settings → Facebook Integration</strong></li>
            <li>Click the <strong>Disconnect</strong> button</li>
            <li>All Facebook tokens and connection data will be immediately removed</li>
          </ol>

          <p className="text-gray-700 leading-relaxed mt-4 mb-3">You can also remove app access from your Facebook account directly:</p>
          <ol className="list-decimal pl-6 space-y-1 text-gray-700">
            <li>Go to <a href="https://www.facebook.com/settings?tab=business_tools" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">Facebook Settings → Business Integrations</a></li>
            <li>Find "Drive On Wheels" in the list</li>
            <li>Click "Remove" to revoke access</li>
          </ol>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Processing Timeline</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="border border-gray-200 rounded-lg p-4 text-center">
              <ClockIcon className="h-8 w-8 text-primary-600 mx-auto mb-2" />
              <p className="font-semibold text-gray-900">Within 24 hours</p>
              <p className="text-sm text-gray-600 mt-1">Acknowledgment of your request</p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4 text-center">
              <ShieldCheckIcon className="h-8 w-8 text-primary-600 mx-auto mb-2" />
              <p className="font-semibold text-gray-900">Within 7 days</p>
              <p className="text-sm text-gray-600 mt-1">Identity verification</p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4 text-center">
              <ClockIcon className="h-8 w-8 text-primary-600 mx-auto mb-2" />
              <p className="font-semibold text-gray-900">Within 30 days</p>
              <p className="text-sm text-gray-600 mt-1">Complete data deletion</p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">What We May Retain</h2>
          <p className="text-gray-700 leading-relaxed mb-3">
            Some information may be retained even after a deletion request, but only when required by:
          </p>
          <ul className="list-disc pl-6 space-y-1 text-gray-700">
            <li>Legal obligations (tax records, audit requirements)</li>
            <li>Pending legal claims or disputes</li>
            <li>Fraud prevention and security</li>
            <li>Statistical/anonymized data that no longer identifies you</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Contact Us</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            For any questions about data deletion or your privacy rights:
          </p>
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-gray-700">
              <EnvelopeIcon className="h-5 w-5 text-primary-600 flex-shrink-0" />
              <a href="mailto:padeyvarun03@gmail.com" className="text-primary-600 hover:underline">padeyvarun03@gmail.com</a>
            </div>
            <div className="flex items-center gap-3 text-gray-700">
              <PhoneIcon className="h-5 w-5 text-primary-600 flex-shrink-0" />
              <a href="tel:+919818864690" className="text-primary-600 hover:underline">+91 9818864690</a>
            </div>
          </div>

          <div className="mt-8 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-sm text-gray-600">
              For more details on how we handle your data, please review our{' '}
              <Link to="/privacy-policy" className="text-primary-600 hover:underline font-medium">Privacy Policy</Link>.
            </p>
          </div>
        </article>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 flex flex-wrap items-center justify-between text-sm text-gray-500">
          <p>&copy; {new Date().getFullYear()} Drive On Wheels. All rights reserved.</p>
          <div className="flex gap-4">
            <Link to="/privacy-policy" className="hover:text-primary-600">Privacy Policy</Link>
            <Link to="/terms-and-conditions" className="hover:text-primary-600">Terms and Conditions</Link>
            <Link to="/data-deletion" className="hover:text-primary-600">Data Deletion</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};
